<script setup>
import { ref, computed } from 'vue'
import dayjs from 'dayjs'
import ja from 'dayjs/locale/ja'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { navigateToSample } from '~/utils/navigateToSample'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.tz.setDefault('Asia/Tokyo')
dayjs.locale(ja)

const { $isPremium: isPremium } = useNuxtApp()
let clearId = null
const isPlaying = ref(false)
const props = defineProps({
  timeList: {
    type: Array,
    default: () => []
  }
})
const selectedTime = defineModel('selectedTime', { type: String })
const sliderValue = ref(props.timeList.findIndex((item) => item.time === selectedTime.value))
const maxIndex = computed(() => props.timeList.length - 1)
const isFirstItem = computed(() => sliderValue.value === 0)
const isLastItem = computed(() => sliderValue.value === maxIndex.value)
const isInvisibleTag = (tm) => {
  const time = dayjs.tz(tm, 'YYYYMMDDHHmm', 'Asia/Tokyo')
  return ![0].includes(time.hour())
}
const selectItem = (index) => {
  if (index >= 0 && index < props.timeList.length) {
    sliderValue.value = index
    selectedTime.value = props.timeList[index].time
  }
}
const anime = () => {
  if (sliderValue.value < maxIndex.value) {
    onClickNext()
  } else {
    selectItem(0)
  }
  if (sliderValue.value === maxIndex.value) {
    clearId = setTimeout(anime, 2000)
  } else {
    clearId = setTimeout(anime, 1000)
  }
}
const onClickNext = () => {
  if (!isPremium.value) {
    navigateToSample()
  } else {
    selectItem(sliderValue.value + 1)
  }
}
const onClickPrev = () => {
  if (!isPremium.value) {
    navigateToSample()
  } else {
    selectItem(sliderValue.value - 1)
  }
}
const onSliderChange = () => {
  if (!isPremium.value) {
    navigateToSample()
    selectItem(0)
  } else {
    selectItem(sliderValue.value)
  }
}
const onClickPlay = () => {
  if (!isPremium.value) {
    navigateToSample()
  } else {
    isPlaying.value = true
    clearTimeout(clearId)
    anime()
  }
}
const onClickStop = () => {
  if (!isPremium.value) {
    navigateToSample()
  } else {
    isPlaying.value = false
    clearTimeout(clearId)
  }
}
const onClickMove = (index) => {
  if (!isPremium.value) {
    navigateToSample()
  } else {
    selectItem(index)
  }
}
</script>

<template>
  <div
    class="slider__inner"
    :style="{ '--time-list-length': props.timeList.length }"
  >
    <div class="controller">
      <div class="anime__button">
        <button
          v-show="!isPlaying"
          data-button="play"
          @click="onClickPlay"
        />
        <button
          v-show="isPlaying"
          data-button="stop"
          @click="onClickStop"
        />
      </div>
      <div class="controller__button">
        <button
          data-button="prev"
          :disabled="isFirstItem"
          @click="onClickPrev"
        />
        <p>
          {{ dayjs.tz(timeList[sliderValue].time, 'YYYYMMDDHHmm', 'Asia/Tokyo').format('M/D (ddd) HH:mm') }} 予想
        </p>
        <button
          data-button="next"
          :disabled="isLastItem"
          @click="onClickNext"
        />
      </div>
    </div>
    <div class="content">
      <div class="range">
        <input
          v-model.number="sliderValue"
          type="range"
          min="0"
          :max="maxIndex"
          step="1"
          @change="onSliderChange"
        >
        <div
          class="list"
        >
          <p
            v-for="(item, index) in timeList"
            :key="index"
            class="scale"
            :invisible-tag="isInvisibleTag(item.time)"
            :start-tag="index < 3"
            :end-tag="index > timeList.length - 4"
          >
            <span
              v-if="!isInvisibleTag(item.time)"
              @click="onClickMove(index)"
            >
              {{ dayjs.tz(item.time, 'YYYYMMDDHHmm', 'Asia/Tokyo').format('D日H時') }}
            </span>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.slider__inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
}
.controller {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 5px;
  width: 100%;
  user-select: none;
  position: relative;
  .anime__button {
    position: absolute;
    top: 0;
    left: 0;
  }
  .controller__button {
    display: flex;
    align-items: center;
    gap: 5px;
    user-select: none;
    p {
      min-width: 150px;
      padding: 6px 15px 5px;
      background-color: #fff;
      border-radius: 100vmax;
      color: #ffa500;
      font-size: 14px;
      line-height: 14px;
      text-align: center;
    }
  }
}
.content {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
}
button {
  width: 25px;
  height: 25px;
  border-radius: 4px;
  background-color: #fff;
  box-shadow: 0 2px 2px 0 rgb(0 0 0 / 0.15);
  background-position: center;
  background-repeat: no-repeat;
  background-size: 12px;
  cursor: pointer;
  &[data-button="prev"] {
    background-image: url("https://gvs.weathernews.jp/onebox/img/common/slider/icon_prev.svg");
  }
  &[data-button="next"] {
    background-image: url("https://gvs.weathernews.jp/onebox/img/common/slider/icon_next.svg");
  }
  &[data-button="play"] {
    background-image: url("https://gvs.weathernews.jp/onebox/img/common/slider/icon_play.svg");
  }
  &[data-button="stop"] {
    background-image: url("https://gvs.weathernews.jp/onebox/img/common/slider/icon_pause.svg");
  }
  &:disabled {
    opacity: 0.4;
    pointer-events: none;
  }
  @media (any-hover: hover) {
    transition: opacity .2s ease-in-out;
    &:hover {
      opacity: .6;
    }
  }
}
.range {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  flex: 1;
  input {
    position: relative;
    -webkit-appearance: none;
    appearance: none;
    cursor: pointer;
    outline: none;
    width: 100%;
    height: 3px;
    margin-inline: auto;
    background-color: #ffa500;
    border-radius: 100vmax;
    border: none;
    z-index: 2;
    &:disabled {
      opacity: 0.4;
      pointer-events: none;
    }
    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      background-color: #ffa500;
      width: 14px;
      height: 14px;
      border-radius: 50%;
      box-shadow: none;
    }
    &::-moz-range-thumb {
      background: #ffa500;
      width: 14px;
      height: 14px;
      border-radius: 50%;
      box-shadow: none;
      border: none;
    }
  }
  .list {
    display: flex;
    user-select: none;
    width: calc((100% - 14px)/(var(--time-list-length) - 1)*var(--time-list-length))
  }
  .scale {
    position: relative;
    flex: 1;
    padding-top: 10px;
    text-align: center;
    font-size: 11px;
    font-weight: 600;
    line-height: 12px;
    color: #303030;
    width: calc(100% / var(--time-list-length));
    white-space: nowrap;
    display: flex;
    justify-content: center;
    &::before {
      position: absolute;
      top: 0;
      left: 50%;
      transform: translateX(-50%);
      width: 1px;
      height: 10px;
      background-color: #8793ae;
      content: '';
    }
    span {
      cursor: pointer;
    }
    &[invisible-tag='true'] {
      font-size: 0;
      color: transparent;
      &::before {
        height: 5px;
      }
      span {
        width: 100%;
        max-width: 30.6px;
      }
    }
    &[start-tag='true'] {
      justify-content: flex-start;
    }
    &[end-tag='true'] {
      justify-content: flex-end;
    }
  }
}
</style>
